import axios from "axios";
import { toast } from "react-toastify";

// Prefer an env override, otherwise use the current host so mobile devices on the same LAN can reach the API.
export const API_BASE_URL = "https://booking.softwarezz.com/api/v1/clinicApi";
// export const API_BASE_URL = "http://localhost:4500/api/v1/clinicApi";



export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // Fail fast on very slow connections so we can surface a clear message.
  timeout: 20000,
});
// (extractMessage & pickMessage are declared earlier in this file)

const NETWORK_TOAST_ID = "network-connectivity-error";

const isNetworkOrTimeoutError = (error: any): boolean => {
  if (!error) return false;
  if (!error.response) return true; // offline / DNS / blocked
  if (error.code && String(error.code).toLowerCase() === "econnaborted")
    return true; // axios timeout
  if (typeof error.message === "string" && error.message.includes("timeout"))
    return true;
  if (typeof error.message === "string" && error.message.includes("Network Error"))
    return true;
  return false;
};

const showErrorToast = (message: string, toastId?: string) => {
  toast.error(String(message), {
    position: "top-right",
    toastId: toastId ? String(toastId) : String(message),
  });
};

const buildNetworkMessage = () => {
  const offline =
    typeof navigator !== "undefined" && navigator && navigator.onLine === false;
  if (offline) return "You appear to be offline. Please check your connection.";
  return "Connection issue detected. Please check your internet and try again.";
};

const handleApiError = (error: any) => {
  try {
    if (isNetworkOrTimeoutError(error)) {
      showErrorToast(buildNetworkMessage(), NETWORK_TOAST_ID);
      return Promise.reject(error);
    }

    const apiError = error?.response?.data;
    const message =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || error?.message || "An error occurred while contacting the server.";

    if (error?.response?.status !== 401) {
      showErrorToast(String(message), message);
    }
  } catch (err) {
    // ignore extraction errors
  }
  return Promise.reject(error);
};

apiClient.interceptors.request.use(
  (config: any) => {
    try {
      const token = localStorage.getItem("api_token");
      if (token) {
        if (!config.headers) config.headers = {} as any;
        config.headers.Authorization = String(token);
      }

      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (config.headers) {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    } catch (e) {}
    return config;
  },
  (error) => Promise.reject(error)
);
// Show a toast for any non-2xx response by default (unless the calling
// code handles the error). We attempt to extract a useful message using
// pickMessage so callers will see the backend's error (e.g. "undefined
// passed as argument #1 for 'whereNot' operation").
apiClient.interceptors.response.use((response) => response, handleApiError);

// Allows other axios instances to reuse the same error handler so
// API errors are shown as toast to users consistently.
export const attachApiErrorHandler = (instance: any) => {
  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => handleApiError(error)
  );
};
export const getAuthToken = (): string | null => {
  return localStorage.getItem("api_token");
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem("refresh_token");
};

export const setTokens = (apiToken: string, refreshToken: string): void => {
  localStorage.setItem("api_token", apiToken);
  localStorage.setItem("refresh_token", refreshToken);
};

export const clearTokens = (): void => {
  localStorage.removeItem("api_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_id");
};

export const getUserId = (): string | null => {
  return localStorage.getItem("user_id");
};

export const setUserId = (userId: string): void => {
  localStorage.setItem("user_id", userId);
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

const generateRandomColor = (): string => {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgb(${r}, ${g}, ${b})`;
};

const extractMessage = (input: any): string | undefined => {
  if (input === undefined || input === null) return undefined;
  if (typeof input === "string") return input;
  if (typeof input === "boolean") return undefined;
  if (Array.isArray(input)) {
    const flattened = input
      .map((item) => extractMessage(item))
      .filter((item): item is string => Boolean(item));
    return flattened.length ? flattened.join(", ") : undefined;
  }
  if (typeof input === "object") {
    const values = Object.values(input);
    for (const value of values) {
      const message = extractMessage(value);
      if (message) return message;
    }
    try {
      return JSON.stringify(input);
    } catch {
      return String(input);
    }
  }
  return String(input);
};

const pickMessage = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    const resolved = extractMessage(candidate);
    if (resolved) return resolved;
  }
  return undefined;
};

export const login = async (email: string, password: string) => {
  try {
    interface LoginResponse {
      api_token: string;
      refresh_token: string;
      id?: string | number;
      userId?: string | number;
      user_id?: string | number;
      [key: string]: any;
    }

    const response = await apiClient.post<LoginResponse>("/login", {
      email,
      password,
    });

    const { api_token, refresh_token, ...userData } = response.data;

    setTokens(api_token, refresh_token);

    const userId = userData.id || userData.userId || userData.user_id;
    if (userId !== undefined && userId !== null) {
      setUserId(userId.toString());
    }

    return {
      success: true,
      token: api_token,
      user: userData,
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_description ||
      error.response?.data?.error ||
      "Login failed";
    return { success: false, error: errorMessage };
  }
};

export const signup = async (
  name: string,
  email: string,
  password: string,
  mobile: string,
  country_code: string,
  color: string = generateRandomColor(),
  bannerFile?: File
) => {
  try {
    const formData = new FormData();

    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("mobile", mobile);
    formData.append("country_code", country_code);
    formData.append("color", color);

    if (bannerFile) {
      formData.append("banner", bannerFile);
    }

    interface SignupResponse {
      api_token: string;
      refresh_token: string;
      id?: string | number;
      userId?: string | number;
      user_id?: string | number;
      [key: string]: any;
    }

    const response = await axios.post<SignupResponse>(
      "https://booking.softwarezz.com/api/v1/clinicApi/register",
      // "http://localhost:4500/api/v1/clinicApi/register",

      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    const { api_token, refresh_token, ...userData } = response.data;
    let message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "";
    if (!message) {
      message = pickMessage(
        (response.data as any)?.status,
        userData?.message,
        userData?.msg,
        userData?.responseMessage
      ) || "";
    }

    setTokens(api_token, refresh_token);

    const userId = userData.id || userData.userId || userData.user_id;
    if (userId !== undefined && userId !== null) {
      setUserId(userId.toString());
    }

    return {
      success: true,
      token: api_token,
      user: userData,
      message,
    };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Registration failed";
    return { success: false, error: errorMessage, message: errorMessage };
  }
};

export const requestPasswordResetCode = async (email: string) => {
  try {
    const response = await apiClient.post("/password/forgot", { email });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Verification code sent";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to send verification code";
    return { success: false, error: errorMessage };
  }
};

export const verifyPasswordResetCode = async (email: string, code: string) => {
  try {
    const response = await apiClient.post("/password/verify-code", {
      email,
      code,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Code verified";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Invalid verification code";
    return { success: false, error: errorMessage };
  }
};

export const resetPasswordWithCode = async (
  email: string,
  code: string,
  password: string,
  passwordConfirmation: string
) => {
  try {
    const response = await apiClient.post("/password/reset", {
      email,
      code,
      password,
      password_confirmation: passwordConfirmation,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Password reset successfully";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to reset password";
    return { success: false, error: errorMessage };
  }
};

export const changePassword = async (
  currentPassword: string,
  password: string,
  passwordConfirmation: string
) => {
  try {
    const response = await apiClient.post("/users/change-password", {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    });
    const message =
      pickMessage(
        (response.data as any)?.message,
        (response.data as any)?.msg,
        (response.data as any)?.responseMessage,
        (response.data as any)?.statusMessage,
        (response.data as any)?.status_text
      ) || "Password updated successfully";
    return { success: true, message };
  } catch (error: any) {
    const apiError = error?.response?.data;
    const errorMessage =
      pickMessage(
        apiError?.error_description,
        apiError?.error,
        apiError?.message,
        apiError?.msg,
        apiError?.responseMessage,
        apiError?.errors,
        apiError?.Message,
        apiError
      ) || "Failed to change password";
    return { success: false, error: errorMessage };
  }
};
